// 상대 경로 직접 import — vite.config(설정 로더)와 앱 양쪽에서 로드되는 파일이라
// 패키지 진입점(@tarot/shared) 대신 파일을 직접 가리킴
import { BRAND } from '../../../../packages/shared/src/brand';

/**
 * ⭐ CI 단일 출처 — 브랜드 마크의 지오메트리와 파비콘이 전부 여기서 나온다.
 * CI를 바꿀 때는 이 폴더(src/brand/)만 수정하면 로고·파비콘·카드 뒷면·서명이 한 번에 바뀜.
 *  - 지오메트리 헬퍼(starPath 등) → BrandLogo.tsx(LogoMark/StarMark)와 faviconSvg가 공용
 *  - 색상은 @tarot/shared BRAND에서만
 * ⚠️ 화면에 🔮 이모지나 개별 SVG로 브랜드 마크를 그리지 말 것 (CLAUDE.md §5)
 */

/** 4각 별(스파클) path — CI의 핵심 모티프 */
export function starPath(cx: number, cy: number, r: number, k = 0.24): string {
  const s = r * k;
  return (
    `M${cx} ${cy - r} L${cx + s} ${cy - s} L${cx + r} ${cy} L${cx + s} ${cy + s} ` +
    `L${cx} ${cy + r} L${cx - s} ${cy + s} L${cx - r} ${cy} L${cx - s} ${cy - s} Z`
  );
}

/** 초승달 path (왼쪽을 바라보는 작은 달) */
export function crescentPath(cx: number, cy: number, r: number): string {
  return (
    `M${cx} ${cy - r} A${r} ${r} 0 1 0 ${cx} ${cy + r} ` +
    `A${r * 0.78} ${r * 0.78} 0 1 1 ${cx} ${cy - r} Z`
  );
}

/**
 * 앱 아이콘 SVG (홈 화면 추가 · PWA · 스토어용) — 파비콘과 다른 규격이 필요하다.
 *
 *  - **모서리를 둥글게 깎지 않는다.** iOS/안드로이드가 알아서 마스킹하므로
 *    여기서 rx를 주면 이중으로 깎여 흰 여백이 생긴다.
 *  - **정사각 정중앙 배치.** 파비콘은 작게 보려고 카드를 위로 올려 두었는데,
 *    큰 아이콘에서는 그게 "가운데가 아닌" 느낌으로 보인다.
 *  - **여백(safe area) 확보.** 마스킹으로 가장자리가 잘려도 마크가 살아남게 한다.
 *  - **선을 굵게.** 큰 크기로 래스터라이즈될 때 얇은 선은 끊어져 보인다.
 *  - **테마 두 벌.** 밝은 배경에 얹는 자리(앱 마켓·앱인토스 콘솔)를 위해 라이트 변형을 낸다.
 *    지오메트리는 하나이고 색만 갈린다 — 두 그림이 어긋나지 않게.
 */
export type MarkTheme = 'dark' | 'light';

export function appIconSvg(theme: MarkTheme = 'dark'): string {
  const light = theme === 'light';
  const gold = light ? BRAND.lightAccentColor : BRAND.accentColor;
  const surface = light ? BRAND.lightThemeColor : BRAND.surfaceColor;
  // 배경 그라디언트 바깥쪽 — 다크는 밤하늘, 라이트는 살짝 따뜻한 종이색
  const backdrop = light ? BRAND.lightSurfaceColor : BRAND.themeColor;
  const S = 512;
  const cx = S / 2;
  const cy = S / 2;

  // 카드 묶음이 정확히 화면 중앙에 오도록 계산 (부채꼴 회전축도 카드 하단 기준)
  const cardW = 158;
  const cardH = 276;
  const cardX = cx - cardW / 2;
  const cardY = cy - cardH / 2;
  const pivot = `${cx} ${cardY + cardH * 0.86}`;

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${S} ${S}">
  <defs>
    <radialGradient id="bg" cx="50%" cy="30%" r="80%">
      <stop offset="0%" stop-color="${surface}"/>
      <stop offset="100%" stop-color="${backdrop}"/>
    </radialGradient>
  </defs>
  <rect width="${S}" height="${S}" fill="url(#bg)"/>
  <g fill="none" stroke="${gold}" stroke-linejoin="round" stroke-linecap="round">
    <g transform="rotate(-19 ${pivot})" opacity="0.8">
      <rect x="${cardX}" y="${cardY + 18}" width="${cardW}" height="${cardH - 18}" rx="14" stroke-width="13"/>
    </g>
    <g transform="rotate(19 ${pivot})" opacity="0.8">
      <rect x="${cardX}" y="${cardY + 18}" width="${cardW}" height="${cardH - 18}" rx="14" stroke-width="13"/>
    </g>
    <rect x="${cardX}" y="${cardY}" width="${cardW}" height="${cardH}" rx="14" fill="${surface}" stroke-width="17"/>
    <rect x="${cardX + 22}" y="${cardY + 22}" width="${cardW - 44}" height="${cardH - 44}" rx="8"
          stroke-width="7" opacity="0.45"/>
  </g>
  <path d="${starPath(cx, cy - 8, 54)}" fill="${gold}"/>
  <path d="${starPath(cx + 168, cy - 168, 26)}" fill="${gold}" opacity="0.9"/>
  <!-- 초승달은 두께가 반지름의 22%뿐이라 아이콘 크기에서 사라진다 → 스파클로 균형 -->
  <path d="${starPath(cx - 172, cy + 158, 18)}" fill="${gold}" opacity="0.7"/>
</svg>`;
}

/**
 * 파비콘 SVG — 부채꼴 카드 3장 + 중앙 별 + 스파클 (레퍼런스 CI 기반).
 * 브라우저 탭처럼 16~32px로 보이는 자리 전용. 앱 아이콘은 appIconSvg를 쓸 것.
 * vite brandPlugin이 dev 서빙 + 빌드 산출물로 사용.
 */
export function faviconSvg(): string {
  const gold = BRAND.accentColor;
  const surface = BRAND.surfaceColor;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
  <rect width="64" height="64" rx="14" fill="${BRAND.themeColor}"/>
  <g transform="rotate(-20 32 44)">
    <rect x="23" y="14" width="18" height="32" rx="3.2" fill="${surface}" stroke="${gold}" stroke-width="2.6" opacity="0.85"/>
  </g>
  <g transform="rotate(20 32 44)">
    <rect x="23" y="14" width="18" height="32" rx="3.2" fill="${surface}" stroke="${gold}" stroke-width="2.6" opacity="0.85"/>
  </g>
  <rect x="23" y="11" width="18" height="34" rx="3.2" fill="${surface}" stroke="${gold}" stroke-width="3.4"/>
  <rect x="26.5" y="14.5" width="11" height="27" rx="1.8" fill="none" stroke="${gold}" stroke-opacity="0.5" stroke-width="1.3"/>
  <path d="${starPath(32, 28, 7.2)}" fill="${gold}"/>
  <path d="${starPath(50, 12, 3.6)}" fill="${gold}" opacity="0.95"/>
  <circle cx="53.5" cy="20.5" r="1.2" fill="${gold}" opacity="0.6"/>
</svg>`;
}
